/**
 * PureEatiz — cart drawer upsell ("Complete your coffee shelf").
 *
 * <pe-cart-upsell> lives inside Horizon's cart drawer, which is re-rendered through the Section
 * Rendering API and morphed after every cart change. The host carries `data-skip-subtree-update`,
 * so the morph only refreshes its data-* attributes and leaves the rendered cards alone; when the
 * cart composition changes (`data-key`), the component re-renders itself from:
 *   1. the Product Recommendations API for the first cart line (intent=complementary, then related),
 *   2. topped up with the server-rendered fallback list (`data-fallback`, collection products),
 * excluding products already in the cart (`data-exclude`).
 *
 * ADD posts to /cart/add.js and announces the change exactly like Horizon's product form does:
 * a `CartLinesUpdateEvent` (action "add") whose promise resolves with the updated cart and the
 * re-rendered `cart-items-component` sections. Horizon's cart-items-component then morphs the
 * drawer, and the header cart bubble / live region update from the same event.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

/** @typedef {{ id: string, title: string, url: string, image: string, variantId: string, variantTitle: string, price: number }} UpsellProduct */

/** @type {Map<string, UpsellProduct[]>} Recommendations per product id (resolved). */
const cache = new Map();
/** @type {Map<string, Promise<UpsellProduct[]>>} */
const inflight = new Map();

const PLUS_ICON =
  '<svg class="pe-icon pe-icon--plus" aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';

/**
 * @param {string} url
 * @param {number} width
 */
function sizedImage(url, width) {
  if (!url) return '';
  const absolute = url.startsWith('//') ? `https:${url}` : url;
  try {
    const parsed = new URL(absolute, window.location.origin);
    parsed.searchParams.set('width', String(width));
    return parsed.toString();
  } catch {
    return absolute;
  }
}

/**
 * Normalises a product from `/recommendations/products.json`.
 * @param {any} product
 * @returns {UpsellProduct | null}
 */
function normalize(product) {
  if (!product || product.available === false) return null;
  const variants = Array.isArray(product.variants) ? product.variants : [];
  const variant = variants.find((v) => v.available) ?? null;
  if (!variant) return null;

  let variantTitle = '';
  if (variants.length > 1 || (variant.public_title && variant.public_title !== 'Default Title')) {
    variantTitle = variant.public_title || variant.title || '';
  } else if (Number(variant.weight) > 0) {
    const grams = Number(variant.weight);
    variantTitle = grams >= 1000 ? `${grams / 1000} kg` : `${grams} g`;
  }

  const image = product.featured_image?.src ?? product.featured_image ?? product.media?.[0]?.src ?? '';

  return {
    id: String(product.id),
    title: product.title,
    url: product.url || `/products/${product.handle}`,
    image: typeof image === 'string' ? sizedImage(image, 160) : '',
    variantId: String(variant.id),
    variantTitle,
    price: Number(variant.price),
  };
}

/**
 * @param {string} baseUrl - routes.product_recommendations_url
 * @param {string} productId
 * @returns {Promise<UpsellProduct[]>}
 */
function loadRecommendations(baseUrl, productId) {
  const cached = cache.get(productId);
  if (cached) return Promise.resolve(cached);

  let pending = inflight.get(productId);
  if (pending) return pending;

  pending = (async () => {
    /** @type {UpsellProduct[]} */
    let list = [];
    for (const intent of ['complementary', 'related']) {
      try {
        const url = `${baseUrl}.json?product_id=${encodeURIComponent(productId)}&limit=10&intent=${intent}`;
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!response.ok) continue;
        const data = await response.json();
        const products = /** @type {UpsellProduct[]} */ ((data?.products ?? []).map(normalize).filter(Boolean));
        list = list.concat(products);
      } catch {
        // Network/API failure: fall back to the server-rendered list.
      }
      // Enough complementary picks to survive the in-cart exclusion — skip "related".
      if (list.length >= 4) break;
    }
    cache.set(productId, list);
    inflight.delete(productId);
    return list;
  })();

  inflight.set(productId, pending);
  return pending;
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

class PeCartUpsell extends HTMLElement {
  static observedAttributes = ['data-key'];

  #scheduled = false;
  /** Variant ids currently rendered (kept in JS: data-* attributes are reset by the morph). */
  #signature = '';

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#schedule(true);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /**
   * The drawer morph updates `data-key` when the cart composition changes.
   * @param {string} _name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue === null || oldValue === newValue || !this.isConnected) return;
    this.#schedule(false);
  }

  /** Waits a microtask so every attribute of the morph has been applied (still before paint). */
  #schedule(initial = false) {
    if (this.#scheduled) return;
    this.#scheduled = true;
    queueMicrotask(() => {
      this.#scheduled = false;
      this.#update(initial);
    });
  }

  /** @param {boolean} initial */
  #update(initial) {
    const productId = this.dataset.productId ?? '';
    const baseUrl = this.dataset.url;

    if (!productId || !baseUrl) {
      if (!initial) this.#render([]);
      return;
    }

    const cached = cache.get(productId);
    if (cached) {
      this.#render(cached);
      return;
    }

    // The server-rendered fallback is already correct on first paint; after a morph the
    // preserved cards may be stale (e.g. the product just added), so redraw from fallback.
    if (!initial) this.#render([]);

    loadRecommendations(baseUrl, productId).then((list) => {
      if (this.isConnected && this.dataset.productId === productId) this.#render(list);
    });
  }

  /** @returns {UpsellProduct[]} */
  #fallback() {
    try {
      const parsed = JSON.parse(this.dataset.fallback || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {UpsellProduct[]} recommendations
   */
  #render(recommendations) {
    const exclude = new Set((this.dataset.exclude ?? '').split(',').filter(Boolean));
    const limit = Number(this.dataset.limit) || 2;
    /** @type {UpsellProduct[]} */
    const picks = [];
    const seen = new Set();

    for (const product of [...recommendations, ...this.#fallback()]) {
      if (picks.length >= limit) break;
      if (!product?.variantId || exclude.has(String(product.id)) || seen.has(String(product.id))) continue;
      seen.add(String(product.id));
      picks.push(product);
    }

    const signature = picks.map((p) => p.variantId).join(',');
    if (this.#signature === signature && this.childElementCount > 0) return;
    this.#signature = signature;

    if (picks.length === 0) {
      this.replaceChildren();
      return;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'pe-upsell';

    const title = document.createElement('p');
    title.className = 'pe-upsell__title';
    title.textContent = this.dataset.title ?? '';

    const list = document.createElement('ul');
    list.className = 'pe-upsell__list';
    list.setAttribute('role', 'list');
    list.append(...picks.map((product) => this.#card(product)));

    wrapper.append(title, list);
    this.replaceChildren(wrapper);
  }

  /** @param {number} cents */
  #money(cents) {
    const currency = this.dataset.currency || 'INR';
    let format = this.dataset.moneyFormat || '{{amount}}';
    if (cents % 100 === 0) format = format.replace(/{{\s*amount\s*}}/, '{{amount_no_decimals}}');
    const html = formatMoney(cents, format, currency);
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent ?? '';
  }

  /** @param {UpsellProduct} product */
  #card(product) {
    const item = document.createElement('li');
    item.className = 'pe-upsell__item';

    const media = document.createElement('a');
    media.className = 'pe-upsell__media';
    media.href = product.url;
    media.tabIndex = -1;
    media.setAttribute('aria-hidden', 'true');
    if (product.image) {
      const img = document.createElement('img');
      img.src = product.image;
      img.alt = '';
      img.width = 58;
      img.height = 73;
      img.loading = 'lazy';
      media.append(img);
    }

    const info = document.createElement('div');
    info.className = 'pe-upsell__info';
    const name = document.createElement('a');
    name.className = 'pe-upsell__name';
    name.href = product.url;
    name.textContent = product.title;
    const meta = document.createElement('p');
    meta.className = 'pe-upsell__meta';
    const price = this.#money(product.price);
    meta.textContent = product.variantTitle ? `${product.variantTitle} · ${price}` : price;
    info.append(name, meta);

    const addLabel = this.dataset.addLabel || 'Add';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pe-upsell__add';
    button.dataset.variantId = product.variantId;
    button.setAttribute('aria-label', `${this.dataset.addAriaLabel || addLabel}: ${product.title}`);
    button.innerHTML = `<span></span>${PLUS_ICON}`;
    /** @type {HTMLElement} */ (button.firstElementChild).textContent = addLabel;

    item.append(media, info, button);
    return item;
  }

  /** @param {MouseEvent} event */
  #onClick = async (event) => {
    const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
    const button = /** @type {HTMLButtonElement | null} */ (target?.closest('button[data-variant-id]') ?? null);
    if (!button || !this.contains(button) || button.disabled) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.remove('is-error');

    // Same section set Horizon's product form requests: every cart-items-component on the page.
    const sectionIds = new Set();
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) sectionIds.add(element.dataset.sectionId);
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: Array.from(sectionIds).join(','),
            sections_url: window.location.pathname,
          }),
        })
      );
      const data = await response.json();
      const cart = await fetchCart();

      if (data.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, itemCount: 0, source: 'pe-cart-upsell' },
        });
        button.classList.add('is-error');
        button.title = data.description || data.message || '';
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: 1,
          sections: data.sections,
          source: 'pe-cart-upsell',
          didError: false,
        },
      });
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      button.classList.add('is-error');
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('pe-cart-upsell')) {
  customElements.define('pe-cart-upsell', PeCartUpsell);
}
